import React, { useCallback, useEffect, useRef, useState } from 'react'
import { CRow, CCol, CCard, CCardBody, CButton } from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilPlus, cilImage } from '@coreui/icons'
import AddForm from './AddForm'
import EditForm from './EditForm'
import AdminPromoTable from './AdminPromoTable'
import AdsManagerService from 'src/service/AdsManagerService'

const PromoCardsPanel = ({ embedded = false, onTotalChange }) => {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [activePage, setActivePage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [columnFilter, setColumnFilter] = useState({})
  const [columnSorter, setColumnSorter] = useState({})
  const [addPromoVisible, setAddPromoVisible] = useState(false)
  const [editPromoId, setEditPromoId] = useState(null)

  const onTotalChangeRef = useRef(onTotalChange)
  useEffect(() => {
    onTotalChangeRef.current = onTotalChange
  }, [onTotalChange])

  const fetchUsers = useCallback(async () => {
    setLoading(true)
    const offset = itemsPerPage * activePage - itemsPerPage
    let params = new URLSearchParams()
    params.append('ads_scope', 'hub')
    Object.keys(columnFilter).forEach((key) => {
      params.append(key, columnFilter[key])
    })
    columnSorter &&
      columnSorter.column !== undefined &&
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)

    try {
      const result = await AdsManagerService.getAds(offset, itemsPerPage, activePage, params)
      setUsers(result.data)
      onTotalChangeRef.current?.(result.data?.total)
    } catch (error) {
      console.error('Error fetching promo cards:', error)
    } finally {
      setLoading(false)
    }
  }, [activePage, itemsPerPage, columnFilter, columnSorter])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        {!embedded && (
          <div>
            <h4 className="mb-1">Promo Cards</h4>
            <div className="text-medium-emphasis">
              Manage the promo cards available to your games, their artwork and placement.
            </div>
          </div>
        )}
        <div className="d-flex flex-wrap gap-2 ms-auto">
          <CButton color="primary" onClick={() => setAddPromoVisible(true)}>
            <CIcon icon={cilPlus} className="me-1" />
            Add Promo Card
          </CButton>
        </div>
      </div>

      <CRow className="g-3 mb-3">
        <CCol sm={6} lg={4}>
          <CCard className="h-100">
            <CCardBody className="d-flex justify-content-between align-items-center">
              <div>
                <div className="text-medium-emphasis small">Total Promo Cards</div>
                <div className="fs-4 fw-semibold">{users?.total ?? 0}</div>
              </div>
              <div
                className="d-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary"
                style={{ width: 42, height: 42 }}
              >
                <CIcon icon={cilImage} size="lg" />
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <CCard>
        <CCardBody>
          <AdminPromoTable
            users={users}
            fetchUsers={fetchUsers}
            setActivePage={setActivePage}
            setItemsPerPage={setItemsPerPage}
            setColumnFilter={setColumnFilter}
            setColumnSorter={setColumnSorter}
            itemsPerPage={itemsPerPage}
            activePage={activePage}
            columnFilter={columnFilter}
            columnSorter={columnSorter}
            loading={loading}
            onEditPromo={setEditPromoId}
          />
        </CCardBody>
      </CCard>

      <AddForm
        visible={addPromoVisible}
        onClose={() => setAddPromoVisible(false)}
        fetchUsers={fetchUsers}
      />

      <EditForm
        promoId={editPromoId}
        visible={!!editPromoId}
        onClose={() => setEditPromoId(null)}
        fetchUsers={fetchUsers}
      />
    </>
  )
}

export default PromoCardsPanel
